import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import { IWarehouseRawMaterial } from "../../../models/warehouseRawMaterial.model";
import PaginationControls from "../../../utils/PaginationControls";
import MaterialHistoryModal from "./MaterialHistoryModal";
import AddStoreInwardModal from "../Store/AddStoreInwardModal";
import { fetchStoreInwardHistoryThunk } from "../../../store/slices/warehouseSlice";
import CreateProductBOM from "../../Products/productOrMaterialBOMModal";
import {
  ClockArrowDown,
  FileClock,
  Pencil,
  ReceiptText,
  Trash2,
} from "lucide-react";
import StockReductionHistoryDrawer from "./StockReductionDrawer";

interface RawMaterialsSectionProps {
  totalRawMaterialItems: number;
  totalRawMaterialPages: number;
  rawMaterialPage: number;
  itemsPerPage: number;
  getRawMaterials: () => Promise<void>;
  handleEdit: (type: "material", item: IWarehouseRawMaterial) => void;
  handleDelete: (
    type: "factory" | "vendor" | "material",
    id: string,
    name: string,
  ) => void;
  handleRawMaterialPageChange: (page: number) => void;
  onSort: (sortBy: string, sortOrder: "asc" | "desc") => void;
}

const RawMaterialsSection: React.FC<RawMaterialsSectionProps> = ({
  totalRawMaterialItems,
  totalRawMaterialPages,
  rawMaterialPage,
  handleEdit,
  handleDelete,
  handleRawMaterialPageChange,
  onSort,
  getRawMaterials,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const rawMaterials = useSelector(
    (state: RootState) => state.warehouse.rawMaterials,
  );
  const { warehouseNamesAndIds } = useSelector(
    (state: RootState) => state.warehouse,
  );
  const [isStockReductionLogsOpen, setIsStockReductionLogsOpen] =
    useState(false);

  const [selectedRawMaterialId, setSelectedRawMaterialId] = useState<
    string | undefined
  >();
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(
    null,
  );
  const [selectedMaterialName, setSelectedMaterialName] = useState<
    string | null
  >(null);

  const [isInwardEditModalOpen, setIsInwardEditModalOpen] = useState(false);
  const [editInwardId, setEditInwardId] = useState<string | null>(null);
  const [editPrefillData, setEditPrefillData] = useState<any>(null);

  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "desc",
  });
  const [isMaterialBomModalOpen, setIsMaterialBomModalOpen] = useState(false);
  const [selectedMaterialBom, setSelectedMaterialBom] = useState<string | null>(
    null,
  );

  const handleStockReductionLogs = (rawMaterialId: string) => {
    setSelectedRawMaterialId(rawMaterialId);
    setIsStockReductionLogsOpen(true);
  };

  const handleSortRequest = (key: string) => {
    let direction: "asc" | "desc" = "desc";

    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }

    setSortConfig({ key, direction });
    onSort(key, direction);
  };

  const renderSortIcon = (key: string) => {
    if (sortConfig.key !== key) {
      return <span className="ml-1 text-gray-400">↕</span>;
    }

    return sortConfig.direction === "asc" ? (
      <span className="ml-1 text-blue-600">↑</span>
    ) : (
      <span className="ml-1 text-blue-600">↓</span>
    );
  };

  const handleOpenEditInward = (inwardId: string, prefillData: any) => {
    setEditInwardId(inwardId);
    setEditPrefillData(prefillData);
    setIsInwardEditModalOpen(true);
  };

  const refreshHistory = () => {
    if (selectedMaterialId) {
      dispatch(
        fetchStoreInwardHistoryThunk({
          productId: selectedMaterialId,
          page: 1,
          limit: 10,
        }),
      );
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            Raw Materials
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Manage inventory, pricing, and warehouse stock
          </p>
        </div>

        <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-xl text-sm font-medium w-fit">
          Total Items: {totalRawMaterialItems}
        </div>
      </div>

      {/* ==================== Desktop Table ==================== */}
      <div className="hidden lg:block w-full overflow-x-auto">
        <table className="w-full table-auto">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[220px]">
                Material
              </th>

              <th
                className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[180px] cursor-pointer"
                onClick={() => handleSortRequest("qty")}
              >
                Quantity {renderSortIcon("qty")}
              </th>

              <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[160px]">
                Rate
              </th>

              <th
                className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[160px] cursor-pointer"
                onClick={() => handleSortRequest("totalPrice")}
              >
                Total {renderSortIcon("totalPrice")}
              </th>

              <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[80px]">
                HSN
              </th>

              <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[70px]">
                GST
              </th>

              <th
                className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[90px] cursor-pointer"
                onClick={() => handleSortRequest("deliveryDays")}
              >
                Delivery {renderSortIcon("deliveryDays")}
              </th>

              <th className="px-4 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-[120px]">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {(rawMaterials || []).map((material) => (
              <tr
                key={material._id}
                className="hover:bg-blue-50/30 transition-colors"
              >
                {/* Material */}
                <td className="px-4 py-4 align-top">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center text-lg shrink-0">
                      📦
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 break-words leading-5">
                        {material.name}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                          SKU: {material.sku || "-"}
                        </span>

                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-md text-[11px] font-semibold ${
                            material.type === "Raw Material"
                              ? "bg-yellow-100 text-yellow-800"
                              : material.type === "Component"
                                ? "bg-purple-100 text-purple-800"
                                : material.type === "Sub-Assembly"
                                  ? "bg-green-100 text-green-800"
                                  : material.type === "Consumable"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {material.type}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>

                {/* Quantity */}
                <td className="px-4 py-4 align-top">
                  <div className="space-y-2 max-w-[180px]">
                    {material.quantity &&
                    Object.keys(material.quantity).length > 0 ? (
                      Object.entries(material.quantity).map(
                        ([id, quantity]) => {
                          const warehouse = warehouseNamesAndIds.find(
                            (w) => w._id === id,
                          );

                          return (
                            <div
                              key={id}
                              className="bg-gray-50 rounded-lg px-2 py-2 text-xs"
                            >
                              <div className="font-medium text-gray-700 truncate">
                                {warehouse?.name || "Unknown"}
                              </div>

                              <div className="text-gray-900 font-semibold">
                                {Number(quantity).toFixed(2)} {material.unit}
                              </div>
                            </div>
                          );
                        },
                      )
                    ) : (
                      <div className="text-gray-400 italic text-xs">
                        No Stock
                      </div>
                    )}
                  </div>
                </td>

                {/* Rate */}
                <td className="px-4 py-4 align-top">
                  <div className="space-y-2 max-w-[160px]">
                    {material.rate && typeof material.rate === "object"
                      ? Object.entries(material.rate).map(([id, rate]) => {
                          const warehouse = warehouseNamesAndIds.find(
                            (w) => w._id === id,
                          );

                          return (
                            <div
                              key={id}
                              className="bg-gray-50 rounded-lg px-2 py-2 text-xs"
                            >
                              <div className="font-medium text-gray-700 truncate">
                                {warehouse?.name || "Unknown"}
                              </div>

                              <div className="text-gray-900 font-semibold">
                                ₹{Number(rate).toLocaleString()}
                              </div>
                            </div>
                          );
                        })
                      : material.rate
                        ? `₹${Number(material.rate).toLocaleString()}`
                        : "-"}
                  </div>
                </td>

                {/* Total */}
                <td className="px-4 py-4 align-top">
                  <div className="space-y-2 max-w-[160px]">
                    {material.totalPrice &&
                    typeof material.totalPrice === "object"
                      ? Object.entries(material.totalPrice).map(
                          ([id, price]) => {
                            const warehouse = warehouseNamesAndIds.find(
                              (w) => w._id === id,
                            );

                            return (
                              <div
                                key={id}
                                className="bg-green-50 rounded-lg px-2 py-2 text-xs"
                              >
                                <div className="font-medium text-gray-700 truncate">
                                  {warehouse?.name || "Unknown"}
                                </div>

                                <div className="font-semibold text-green-700">
                                  ₹{Number(price).toLocaleString()}
                                </div>
                              </div>
                            );
                          },
                        )
                      : material.totalPrice
                        ? `₹${Number(material.totalPrice).toLocaleString()}`
                        : "-"}
                  </div>
                </td>

                <td className="px-4 py-4 text-sm text-gray-700 align-top">
                  {material.hsn ?? "-"}
                </td>

                <td className="px-4 py-4 text-sm text-gray-700 align-top">
                  {material.gst ?? "-"}%
                </td>

                <td className="px-4 py-4 text-sm text-gray-700 align-top">
                  {material.approxDeliveryDays}d
                </td>

                {/* Actions */}
                <td className="px-4 py-4 align-top">
                  <div className="flex items-center justify-end gap-2">
                    {/* History */}
                    <div className="relative group">
                      <button
                        className="h-8 w-8 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-all text-sm flex items-center justify-center"
                        onClick={() => {
                          setSelectedMaterialId(material._id);
                          setSelectedMaterialName(material.name);
                          setIsHistoryModalOpen(true);
                        }}
                      >
                        <FileClock size={15} />
                      </button>

                      <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                        <div className="bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg">
                          View Inward History
                        </div>
                      </div>
                    </div>

                    {/* BOM */}
                    <div className="relative group">
                      <button
                        className="h-8 w-8 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-all text-sm flex items-center justify-center"
                        onClick={() => {
                          // Add BOM action here
                          setSelectedMaterialName(material.name);
                          setSelectedMaterialId(material._id);
                          setSelectedMaterialBom(material?.bomId || null);
                          setIsMaterialBomModalOpen(true);
                        }}
                      >
                        <ReceiptText size={15} />
                      </button>

                      <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                        <div className="bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg">
                          View BOM
                        </div>
                      </div>
                    </div>

                    {/* Edit */}
                    <div className="relative group">
                      <button
                        className="h-8 w-8 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-all text-sm flex items-center justify-center"
                        onClick={() => handleEdit("material", material)}
                      >
                        <Pencil size={15} />
                      </button>

                      <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                        <div className="bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg">
                          Edit Material
                        </div>
                      </div>
                    </div>
                    {/* stock reduction logs */}
                    <div className="relative group">
                      <button
                        className="h-8 w-8 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-all text-sm flex items-center justify-center"
                        onClick={() => handleStockReductionLogs(material._id)}
                      >
                        <ClockArrowDown size={15} />
                      </button>

                      <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                        <div className="bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg">
                          View Stock Reduction logs
                        </div>
                      </div>
                    </div>

                    {/* Delete */}
                    <div className="relative group">
                      <button
                        className="h-8 w-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-all text-sm flex items-center justify-center"
                        onClick={() =>
                          handleDelete("material", material._id, material.name)
                        }
                      >
                        <Trash2 size={15} />
                      </button>

                      <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                        <div className="bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg">
                          Delete Material
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ==================== Mobile / Tablet Cards ==================== */}
      <div className="lg:hidden divide-y divide-gray-100">
        {(rawMaterials || []).map((material) => (
          <div
            key={material._id}
            className="p-4 sm:p-5 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center text-lg shrink-0">
                📦
              </div>

              <div className="flex-1 min-w-0">
                {/* Header */}
                <div className="flex flex-col gap-2">
                  <div className="text-sm font-semibold text-gray-900 break-words">
                    {material.name}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                      SKU: {material.sku || "-"}
                    </span>

                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-md text-[11px] font-semibold ${
                        material.type === "Raw Material"
                          ? "bg-yellow-100 text-yellow-800"
                          : material.type === "Component"
                            ? "bg-purple-100 text-purple-800"
                            : material.type === "Sub-Assembly"
                              ? "bg-green-100 text-green-800"
                              : material.type === "Consumable"
                                ? "bg-red-100 text-red-800"
                                : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {material.type}
                    </span>
                  </div>
                </div>

                {/* Quantity */}
                <div className="mt-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    Quantity
                  </div>

                  <div className="space-y-2">
                    {material.quantity &&
                    Object.keys(material.quantity).length > 0 ? (
                      Object.entries(material.quantity).map(
                        ([id, quantity]) => {
                          const warehouse = warehouseNamesAndIds.find(
                            (w) => w._id === id,
                          );

                          return (
                            <div
                              key={id}
                              className="bg-gray-50 rounded-lg px-3 py-2"
                            >
                              <div className="text-xs font-medium text-gray-700">
                                {warehouse?.name || "Unknown"}
                              </div>

                              <div className="text-sm font-semibold text-gray-900 mt-1">
                                {Number(quantity).toFixed(2)} {material.unit}
                              </div>
                            </div>
                          );
                        },
                      )
                    ) : (
                      <div className="text-xs text-gray-400 italic">
                        No Stock
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom Stats */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-[11px] text-gray-500 uppercase">
                      HSN
                    </div>

                    <div className="text-sm font-semibold text-gray-900 mt-1">
                      {material.hsn ?? "-"}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-[11px] text-gray-500 uppercase">
                      GST
                    </div>

                    <div className="text-sm font-semibold text-gray-900 mt-1">
                      {material.gst ?? "-"}%
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-[11px] text-gray-500 uppercase">
                      Delivery
                    </div>

                    <div className="text-sm font-semibold text-gray-900 mt-1">
                      {material.approxDeliveryDays}d
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-[11px] text-gray-500 uppercase">
                      Rate
                    </div>

                    <div className="text-sm font-semibold text-gray-900 mt-1 truncate">
                      ₹
                      {typeof material.rate === "number"
                        ? Number(material.rate).toLocaleString()
                        : "-"}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 mt-5 flex-wrap">
                  {/* History */}
                  <div className="relative group">
                    <button
                      className="h-8 w-8 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-all text-sm flex items-center justify-center"
                      onClick={() => {
                        setSelectedMaterialId(material._id);
                        setSelectedMaterialName(material.name);
                        setIsHistoryModalOpen(true);
                      }}
                    >
                      <FileClock size={15} />
                    </button>

                    <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                      <div className="bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg">
                        View Inward History
                      </div>
                    </div>
                  </div>

                  {/* BOM */}
                  <div className="relative group">
                    <button
                      className="h-8 w-8 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-all text-sm flex items-center justify-center"
                      onClick={() => {
                        // Add BOM action here
                        setSelectedMaterialName(material.name);
                        setSelectedMaterialId(material._id);
                        setSelectedMaterialBom(material?.bomId || null);
                        setIsMaterialBomModalOpen(true);
                      }}
                    >
                      <ReceiptText size={15} />
                    </button>

                    <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                      <div className="bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg">
                        View BOM
                      </div>
                    </div>
                  </div>

                  {/* Edit */}
                  <div className="relative group">
                    <button
                      className="h-8 w-8 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-all text-sm flex items-center justify-center"
                      onClick={() => handleEdit("material", material)}
                    >
                      <Pencil size={15} />
                    </button>

                    <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                      <div className="bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg">
                        Edit Material
                      </div>
                    </div>
                  </div>
                  {/* stock reduction logs */}
                  <div className="relative group">
                    <button
                      className="h-8 w-8 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-all text-sm flex items-center justify-center"
                      onClick={() => handleStockReductionLogs(material._id)}
                    >
                      <ClockArrowDown size={15} />
                    </button>

                    <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                      <div className="bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg">
                        View Stock Reduction logs
                      </div>
                    </div>
                  </div>

                  {/* Delete */}
                  <div className="relative group">
                    <button
                      className="h-8 w-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-all text-sm flex items-center justify-center"
                      onClick={() =>
                        handleDelete("material", material._id, material.name)
                      }
                    >
                      <Trash2 size={15} />
                    </button>

                    <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                      <div className="bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg">
                        Delete Material
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
        <PaginationControls
          currentPage={rawMaterialPage}
          totalPages={totalRawMaterialPages}
          onPageChange={handleRawMaterialPageChange}
          totalItems={totalRawMaterialItems}
        />
      </div>
      {/* Stock reduction drawer */}
      <StockReductionHistoryDrawer
        open={isStockReductionLogsOpen}
        onClose={() => {
          setIsStockReductionLogsOpen(false);
          setSelectedRawMaterialId(undefined);
        }}
        rawmaterialId={selectedRawMaterialId}
      />
      {/* History Modal */}
      <MaterialHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => {
          setIsHistoryModalOpen(false);
          setSelectedMaterialId(null);
          setSelectedMaterialName(null);
        }}
        productId={selectedMaterialId}
        materialName={selectedMaterialName}
        onEditInward={handleOpenEditInward}
      />

      {/* Edit Inward Modal */}
      <AddStoreInwardModal
        isOpen={isInwardEditModalOpen}
        onClose={() => {
          setIsInwardEditModalOpen(false);
          setEditInwardId(null);
          setEditPrefillData(null);
        }}
        onSuccess={refreshHistory}
        editInwardId={editInwardId}
        prefillData={editPrefillData}
      />

      {/* Product BOM Modal */}
      {isMaterialBomModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[110]">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 flex justify-between items-center">
              <h2 className="text-lg font-semibold">
                Create BOM for {selectedMaterialName}
              </h2>
              <button
                onClick={() => {
                  setIsMaterialBomModalOpen(false);

                  // setIsEdit(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <CreateProductBOM
              from="material"
              title={selectedMaterialName || "Material"}
              isEdit={!!selectedMaterialBom}
              selectedBomId={selectedMaterialBom}
              selectedProductId={selectedMaterialId}
              onCancel={() => {
                setIsMaterialBomModalOpen(false);
                setSelectedMaterialId(null);
                setSelectedMaterialName(null);
                setSelectedMaterialBom(null);
              }}
              onSuccess={async () => {
                setIsMaterialBomModalOpen(false);

                setSelectedMaterialBom(null);

                await getRawMaterials();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RawMaterialsSection;
